import {
  Bell, BookOpenCheck, Building2, CalendarDays, ChartNoAxesCombined,
  CircleDollarSign, ClipboardCheck, ConciergeBell, Download, Grid2X2, House, LogIn, LogOut, Menu, Settings,
  Sparkles, Users, WashingMachine, X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { GlobalSearch } from "./GlobalSearch";
import { usePwa } from "./PwaProvider";
import { BookingModal } from "./BookingModal";
import { api } from "../lib/api";
import { getCurrentUser, hasPermission, logout } from "../lib/auth";
import { staffRoles } from "../lib/staffRoles";
import { OperationalShiftControl } from "./OperationalShiftControl";
import { ShiftStartGuide, forgetShiftStartGuide } from "./ShiftStartGuide";
import { EntityModalHost } from "./EntityModalHost";
import { PaymentModal } from "./PaymentModal";
import { SoundNotificationControl } from "./SoundNotificationControl";
import type { DashboardSummary } from "@apartos/contracts";

const navigation: ReadonlyArray<{ to: string; label: string; icon: LucideIcon; permission?: string | string[]; roles?: string[]; badge?: string }> = [
  { to: "/dashboard", label: "Dashboard", icon: Grid2X2 },
  { to: "/calendar", label: "Календарь", icon: CalendarDays, permission: "bookings.manage" },
  { to: "/apartments", label: "Апартаменты", icon: Building2, permission: "apartments.manage" },
  { to: "/bookings", label: "Бронирования", icon: BookOpenCheck, permission: "bookings.manage" },
  { to: "/guests", label: "Гости", icon: Users, permission: "guests.manage" },
  { to: "/checkinout", label: "Check-in / out", icon: LogIn, permission: "stays.manage" },
  { to: "/shift", label: "Смена", icon: ClipboardCheck, permission: "cash.expenses.manage" },
  { to: "/cleaning", label: "Уборка", icon: WashingMachine, permission: ["housekeeping.manage", "housekeeping.work"] },
  { to: "/room-service", label: "Рум-сервис", icon: ConciergeBell, permission: ["room_service.manage", "room_service.work"] },
  { to: "/payments", label: "Платежи", icon: CircleDollarSign, permission: ["payments.manage", "payments.collect"] },
  { to: "/reports", label: "Отчёты", icon: ChartNoAxesCombined, permission: "reports.view" },
  { to: "/employees", label: "Сотрудники", icon: Users, permission: ["team.view", "team.manage", "team.operations.manage"] },
  { to: "/settings", label: "Настройки", icon: Settings, permission: "settings.manage" },
];

const pageMeta: Record<string, [string, string]> = {
  dashboard: ["Сегодня", "Главный экран администратора"],
  calendar: ["Календарь бронирований", "Дорожки и календарь месяца"],
  apartments: ["Апартаменты", "Номерной фонд"],
  bookings: ["Бронирования", "Все брони и статусы оплат"],
  guests: ["Гости", "CRM клиентов"],
  checkinout: ["Заезды и выезды", "Сегодня и незакрытые просрочки"],
  shift: ["Смена", "Задачи, расходы и передача кассы"],
  cleaning: ["Уборка", "Задачи горничных"],
  "room-service": ["Рум-сервис", "Заказы гостей и доставка"],
  payments: ["Платежи", "Финансовые операции"],
  reports: ["Отчёты", "Аналитика загрузки и дохода"],
  employees: ["Сотрудники", "Команда и права доступа"],
  settings: ["Настройки", "Конфигурация системы"],
};

export function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<DashboardSummary["attention"][number] | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canInstall, install } = usePwa();
  const auth = useQuery({ queryKey: ["auth", "me"], queryFn: getCurrentUser, staleTime: 60_000 });
  const currentUser = auth.data && auth.data !== "bootstrap" ? auth.data : null;
  const can = (permission?: string | string[]) => !permission || hasPermission(currentUser, permission);
  const hasOperationalDashboard = Boolean(currentUser && ["owner", "manager", "administrator", "concierge"].includes(currentUser.role));
  const dashboard = useQuery({ queryKey: ["dashboard", "summary"], queryFn: api.dashboard, enabled: auth.isSuccess && hasOperationalDashboard });
  const notifications = dashboard.data?.attention ?? [];
  const key = location.pathname.split("/")[1] || "dashboard";
  const [title, subtitle] = pageMeta[key] ?? ["Qadim Yard", "Управление резиденцией"];

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">Перейти к содержимому</a>
      <aside className={`sidebar ${mobileOpen ? "sidebar--open" : ""}`}>
        <div className="brand">
          <div className="brand__mark"><House size={19} strokeWidth={2.1} /></div>
          <div>
            <div className="brand__name">Qadim <span>Yard</span></div>
            <div className="brand__property">{currentUser?.propertyName ?? "Qadim Yard"}</div>
          </div>
          <button className="icon-button sidebar__close" onClick={() => setMobileOpen(false)} aria-label="Закрыть меню"><X size={18} /></button>
        </div>
        <nav className="navigation" aria-label="Основная навигация">
          {navigation.filter((item) => can(item.permission) && (item.to !== "/dashboard" || hasOperationalDashboard)).map(({ to, label, icon: Icon, badge }) => (
            <NavLink key={to} to={to} onClick={() => setMobileOpen(false)} className={({ isActive }) => `nav-item ${isActive ? "nav-item--active" : ""}`}>
              <Icon size={19} strokeWidth={1.8} />
              <span>{label}</span>
              {badge && <b>{badge}</b>}
            </NavLink>
          ))}
        </nav>
        {canInstall && (
          <button className="pwa-install" type="button" onClick={() => void install()}>
            <span className="pwa-install__icon"><Download size={16} /></span>
            <span><strong>Установить Qadim Yard</strong><small>Открывать как приложение</small></span>
          </button>
        )}
        <div className="profile">
          <div className="avatar">{currentUser ? currentUser.name.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase() : "АД"}</div>
          <div className="profile__copy"><strong>{currentUser?.name ?? "Администратор"}</strong><span>{currentUser ? staffRoles[currentUser.role].label : "Полный доступ"}</span></div>
          {currentUser
            ? <button className="profile__logout" aria-label="Выйти из системы" title="Выйти" onClick={async () => {
              await logout();
              forgetShiftStartGuide();
              queryClient.setQueryData(["auth", "me"], null);
              queryClient.clear();
              navigate("/");
            }}><LogOut size={15} /></button>
            : <Sparkles size={16} />}
        </div>
      </aside>

      {mobileOpen && <button className="sidebar-backdrop" onClick={() => setMobileOpen(false)} aria-label="Закрыть меню" />}

      <div className="workspace">
        <header className="topbar">
          <button className="icon-button mobile-menu" onClick={() => setMobileOpen(true)} aria-label="Открыть меню"><Menu size={19} /></button>
          <div className="page-title"><strong>{title}</strong><span>{subtitle}</span></div>
          <div className="topbar__spacer" />
          {currentUser && ["owner", "manager", "administrator", "concierge"].includes(currentUser.role) && <OperationalShiftControl user={currentUser} />}
          {can(["bookings.manage", "guests.manage", "stays.manage"]) && <GlobalSearch />}
          {can("bookings.manage") && <button className="primary-button" onClick={() => setBookingOpen(true)}><span>＋</span> Бронь</button>}
          {can(["payments.manage", "payments.collect"]) && <button className="secondary-button" onClick={() => setPaymentOpen(true)}><CircleDollarSign size={16} /> Оплата</button>}
          <SoundNotificationControl user={currentUser} />
          <div className="notification-wrap"><button className="icon-button notification" onClick={() => setNotificationsOpen((value) => !value)} aria-label={`Уведомления: ${notifications.length}`}><Bell size={17} />{notifications.length > 0 && <i />}</button>
            {notificationsOpen && <div className="notification-panel"><div><strong>Задачи · {notifications.length}</strong><button onClick={() => setNotificationsOpen(false)}>Закрыть</button></div>
              {notifications.map((item) => <button className="notification-item" key={item.id} onClick={() => { setNotificationsOpen(false); setSelectedTask(item); }}><i className={`bg-${item.tone}`} /><span><strong>{item.title}</strong><small>{item.subtitle}</small></span></button>)}
              {notifications.length === 0 && <div className="empty-state"><Bell size={20} /><h2>Всё спокойно</h2><p>Оплаты и операции не требуют внимания.</p></div>}
            </div>}
          </div>
        </header>
        <main id="main-content" className="content" tabIndex={-1}><Outlet /></main>
      </div>
      {bookingOpen && <BookingModal onClose={() => setBookingOpen(false)} />}
      {paymentOpen && <PaymentModal onClose={() => setPaymentOpen(false)} />}
      {currentUser && <ShiftStartGuide key={currentUser.id} user={currentUser} />}
      {selectedTask && <EntityModalHost task={selectedTask} onClose={() => setSelectedTask(null)} onOpenSection={(path) => { setSelectedTask(null); navigate(path); }} />}
    </div>
  );
}
